import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { CheckCircle, Clock, Eye } from 'lucide-react';
import ValidationCotationCompare from './ValidationCotationCompare';

export default function AdminValidationRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending'); // 'pending', 'in_progress', 'completed', 'all'

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadRequests();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const loadRequests = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('verification_requests')
        .select(`
          *,
          client:profiles!client_id(email, full_name),
          artwork:artworks(title, artist, images)
        `)
        .order('created_at', { ascending: false });

      if (filter !== 'all') {
        query = query.eq('status', filter);
      }

      const { data, error } = await query;
      if (error) throw error;
      setRequests(data || []);
    } catch (error) {
      console.error('Erreur chargement demandes:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateRequestStatus = async (requestId, newStatus, notes = '') => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('verification_requests')
        .update({
          status: newStatus,
          validated_by: user.id,
          validated_at: new Date().toISOString(),
          notes: notes,
          updated_at: new Date().toISOString()
        })
        .eq('id', requestId);

      if (error) throw error;
      
      alert(`✅ Statut mis à jour : ${newStatus}`);
      loadRequests();
    } catch (error) {
      console.error('Erreur mise à jour:', error);
      alert('❌ Erreur lors de la mise à jour');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      in_progress: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    
    const labels = {
      pending: 'En attente',
      in_progress: 'En cours',
      completed: 'Validée',
      rejected: 'Rejetée'
    };

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const getMethodLabel = (type) => {
    const labels = {
      estimation_points: 'Méthode par points',
      estimation_comparables: 'Œuvres comparables',
      fallback_non_cote: 'Fallback prix format'
    };
    return labels[type] || type;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-serif font-bold">Demandes de validation</h2>
        <button
          onClick={loadRequests}
          className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition"
        >
          🔄 Actualiser
        </button>
      </div>

      {/* Filtres */}
      <div className="flex gap-2">
        {[
          { value: 'pending', label: 'En attente', icon: Clock },
          { value: 'in_progress', label: 'En cours', icon: Eye },
          { value: 'completed', label: 'Validées', icon: CheckCircle },
          { value: 'all', label: 'Toutes', icon: null }
        ].map(({ value, label, icon: Icon }) => (
          <button
            key={value}
            onClick={() => setFilter(value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition flex items-center gap-2 ${
              filter === value
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {Icon && <Icon size={16} />}
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="text-4xl mb-4">⏳</div>
          <p className="text-gray-600">Chargement des demandes...</p>
        </div>
      ) : requests.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-600">Aucune demande trouvée pour ce filtre</p>
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map((request) => (
            <ValidationRequestCard
              key={request.id}
              request={request}
              onUpdateStatus={updateRequestStatus}
              getStatusBadge={getStatusBadge}
              getMethodLabel={getMethodLabel}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Composant carte pour chaque demande
function ValidationRequestCard({ request, onUpdateStatus, getStatusBadge, getMethodLabel }) {
  const [expanded, setExpanded] = useState(false);
  const [notes, setNotes] = useState('');

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="text-lg font-semibold">
                {request.artwork?.title || 'Œuvre sans titre'}
              </h3>
              {getStatusBadge(request.status)}
            </div>
            <div className="text-sm text-gray-600 space-y-1">
              <p>👤 Client : {request.client?.full_name || request.client?.email}</p>
              <p>🎨 Artiste : {request.artwork?.artist || 'Non renseigné'}</p>
              <p>📊 Méthode : {getMethodLabel(request.type)}</p>
              <p>📅 Demandé le : {formatDate(request.created_at)}</p>
              <p>💰 Coût : {request.cost === 0 ? 'Gratuit (quota inclus)' : `${request.cost} €`}</p>
              <p>💳 Plan : <span className="font-medium uppercase">{request.plan}</span></p>
            </div>
          </div>

          {/* Image */}
          {request.artwork?.images?.[0] && (
            <div className="w-24 h-24 bg-gray-100 rounded-lg overflow-hidden ml-4">
              <img
                src={request.artwork.images[0].url || request.artwork.images[0].path}
                alt={request.artwork.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}
        </div>

        {/* Bouton détails */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-sm text-blue-600 hover:underline"
        >
          {expanded ? '▼ Masquer les détails' : '▶ Voir les détails de l estimation'}
        </button>
      </div>

      {/* Détails expandables */}
      {expanded && (
        <div className="border-t border-gray-200 bg-gray-50 p-6">
          <h4 className="font-semibold mb-3">Données de l'estimation</h4>
          <pre className="bg-white p-4 rounded border text-xs overflow-auto max-h-96">
            {JSON.stringify(request.data, null, 2)}
          </pre>

          <ValidationCotationCompare artworkId={request.artwork_id} clientData={request.data} />

          {request.notes && (
            <div className="mt-4">
              <h4 className="font-semibold mb-2">Notes</h4>
              <p className="text-sm text-gray-700 bg-white p-3 rounded border">
                {request.notes}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      {request.status === 'pending' && (
        <div className="border-t border-gray-200 bg-white p-6">
          <h4 className="font-semibold mb-3">Actions</h4>
          
          <textarea
            placeholder="Notes optionnelles (visibles par le client)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-3 text-sm"
            rows="3"
          />

          <div className="flex gap-3">
            <button
              onClick={() => onUpdateStatus(request.id, 'in_progress', notes)}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              📝 Prendre en charge
            </button>
            <button
              onClick={() => onUpdateStatus(request.id, 'completed', notes)}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
            >
              ✅ Valider et envoyer
            </button>
            <button
              onClick={() => {
                if (window.confirm('Êtes-vous sûr de vouloir rejeter cette demande ?')) {
                  onUpdateStatus(request.id, 'rejected', notes || 'Rejetée par l\'équipe');
                }
              }}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
            >
              ❌ Rejeter
            </button>
          </div>
        </div>
      )}

      {request.status === 'in_progress' && (
        <div className="border-t border-gray-200 bg-blue-50 p-6">
          <div className="flex gap-3">
            <button
              onClick={() => onUpdateStatus(request.id, 'completed', notes)}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
            >
              ✅ Marquer comme validée
            </button>
          </div>
        </div>
      )}
    </div>
  );
}